import React from 'react';
import { FormInstance, Table } from 'antd';
import { Currency, InfoTooltip, Text } from '@common/components';
import { StaffingRequestType } from '@jobs/graphql';
import { RatesByShiftType } from '@institutions/services';
type EstimatedCostsType = {
  templateEstimatedCost: number;
  travelerEstimatedCost: number;
  localEstimatedCost: number;
  directSourcingEstimatedCost: number;
  fieldRateEstimatedCost: number;
};

type EditRatesEstimatedCostTableProps = {
  form: FormInstance;
  estimatedCosts: EstimatedCostsType;
  effectiveBillingCard: boolean;
  staffingRequest: StaffingRequestType;
  billingCardRates: RatesByShiftType;
  deviated: boolean;
};

export function EditRatesEstimatedCostTable({
  form,
  estimatedCosts,
  effectiveBillingCard,
  staffingRequest,
  billingCardRates,
  deviated,
}: EditRatesEstimatedCostTableProps) {
  const isOnDemand = staffingRequest.staffingRequestType === 'on_demand';

  const dataSource = [
    {
      employmentType: (
        <>
          <Text>Custom Rate</Text>
          <InfoTooltip
            size="md"
            leftOffset="sm"
            title="This Staffing Request’s rate is different from the rate card’s rate."
            kind="warn"
            iconColor="sunDarker"
          />
        </>
      ),
      rate: (
        <Text weight="bold">
          <Currency value={form.getFieldValue('rate')} />
        </Text>
      ),
      estimatedCost: (
        <Text weight="bold">
          <Currency value={estimatedCosts.fieldRateEstimatedCost || 0} />
        </Text>
      ),
      visible: deviated,
    },
    {
      employmentType: 'Template',
      rate: (
        <Text
          deleted={(effectiveBillingCard && !deviated) || deviated}
          secondary={(effectiveBillingCard && !deviated) || deviated}
        >
          <Currency
            value={staffingRequest.staffingRequestTemplate.billingRegularRate}
          />
        </Text>
      ),
      estimatedCost: (
        <Text
          deleted={(effectiveBillingCard && !deviated) || deviated}
          secondary={(effectiveBillingCard && !deviated) || deviated}
        >
          <Currency value={estimatedCosts.templateEstimatedCost || 0} />
        </Text>
      ),
      visible: true,
    },
    {
      employmentType: 'Traveler',
      rate: (
        <Text deleted={deviated} secondary={deviated}>
          <Currency value={billingCardRates.travelerRate || 0} />
        </Text>
      ),
      estimatedCost: (
        <Text deleted={deviated} secondary={deviated}>
          <Currency value={estimatedCosts.travelerEstimatedCost || 0} />
        </Text>
      ),
      visible: effectiveBillingCard && !isOnDemand,
    },
    {
      employmentType: 'Local',
      rate: (
        <Text deleted={deviated} secondary={deviated}>
          <Currency value={billingCardRates.localRate || 0} />
        </Text>
      ),
      estimatedCost: (
        <Text deleted={deviated} secondary={deviated}>
          <Currency value={estimatedCosts.localEstimatedCost || 0} />
        </Text>
      ),
      visible: effectiveBillingCard && !isOnDemand,
    },
    {
      employmentType: 'Direct Sourcing',
      rate: (
        <Text deleted={deviated} secondary={deviated}>
          <Currency value={billingCardRates.directSourcingRate || 0} />
        </Text>
      ),
      estimatedCost: (
        <Text deleted={deviated} secondary={deviated}>
          <Currency value={estimatedCosts.directSourcingEstimatedCost || 0} />
        </Text>
      ),
      visible: effectiveBillingCard && !isOnDemand,
    },
  ].filter((item) => item.visible);

  return (
    <Table
      pagination={false}
      dataSource={dataSource}
      columns={[
        {
          title: 'Employment Type',
          dataIndex: 'employmentType',
          width: '50%',
        },
        {
          title: 'Rate',
          dataIndex: 'rate',
          width: '25%',
        },
        {
          title: 'Estimated Cost',
          dataIndex: 'estimatedCost',
          width: '25%',
        },
      ]}
    />
  );
}
